import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { primaryText } from '../../../styling/stylingHelper';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { InventorySection } from '../../../services/inventory/inventoryService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  restaurantId: string;
  /** Group the new section is created in (required for create mode). */
  groupId?: string;
  section?: InventorySection;
}

export default function SaveInventorySectionModal({ isOpen, onClose, mode, restaurantId, groupId, section }: Props) {
  const { t } = useTranslation();
  const { createSection, updateSection, sections } = useInventoryStore();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(mode === 'edit' && section ? section.sectionName : '');
    setError(null);
    setSaving(false);
  }, [isOpen, mode, section]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('inventory.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === 'create') {
        if (!groupId) {
          setError(t('inventory.nameRequired'));
          setSaving(false);
          return;
        }
        const groupSections = sections.filter((s) => s.groupId === groupId);
        const nextPosition = groupSections.reduce((m, s) => Math.max(m, s.position), -1) + 1;
        await createSection({ restaurantId, groupId, sectionName: trimmed, position: nextPosition });
      } else if (section) {
        await updateSection(section.id, { sectionName: trimmed });
      }
      onClose();
    } catch (err: unknown) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to save inventory section');
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/60 dark:bg-black/60 transition-opacity duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-[584px]',
          'bg-white dark:bg-[#0A2133] shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E8E8E9] dark:border-[#1E3A4E] flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-[#13354F] hover:bg-gray-50 dark:bg-[#13354F] transition-colors"
            aria-label={t('inventory.cancel') as string}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dark:text-[#CBD5E1] text-[#000]" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8337 10H4.16699" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.0003 15.8332L4.16699 9.99984L10.0003 4.1665" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-5 sm:pt-5 py-3 sm:pb-5">
          <h2 className="text-[24px] sm:text-[28px] leading-9 sm:leading-[39px] font-bold text-[#161924] dark:text-zinc-50 mb-5 sm:mb-6">
            {mode === 'create' ? t('inventory.createSection') : t('inventory.editSection')}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="rounded-lg border border-[#E8E8E9] dark:border-[#1E3A4E] p-3 sm:p-4 bg-[#F9F9FA] dark:bg-[#0E2B42]">
            <label className={cn(primaryText, 'text-[16px] dark:text-[#CBD5E1] leading-[28px] font-medium block mb-3')}>
              {t('inventory.sectionName')}
            </label>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('inventory.sectionNamePlaceholder') as string}
              className={cn(
                'w-full px-4 h-11 rounded-xl border border-[#D0D5DD] dark:border-[#1E3A4E] text-[14px] leading-5 font-semibold placeholder:text-[#667085]',
                'bg-white dark:bg-[#081C2C]',
                'text-zinc-900 dark:text-[#CBD5E1]',
                'focus:outline-none'
              )}
            />
          </div>
        </div>

        <div className="flex gap-3 sm:gap-6 p-4 sm:p-5 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-6 h-11 rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] hover:bg-gray-50 dark:bg-[#14344C] disabled:opacity-50 transition-colors"
          >
            {t('inventory.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-6 h-11 rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-50 transition-colors"
          >
            {saving ? t('inventory.loading') : mode === 'create' ? t('inventory.createSectionConfirm') : t('inventory.save')}
          </button>
        </div>
      </div>
    </>
  );
}
